// contexts/LocationContext.tsx
import { createContext, useContext, useState, useEffect } from "react";
import AuthService from "@/lib/auth";
import { resolveStateName } from "@/lib/utils";

export interface LocationOption {
  value: number;
  label: string;
  branchId: string;
  branchName: string;
  hospitalName: string;
  hospitalId?: number | string;
  stateName?: string;
  phone?: string;
  city?: string;
}

interface LocationContextType {
  locations: LocationOption[];
  selectedLocationId: number | undefined;
  selectedHospitalName: string | undefined;
  loading: boolean;
  setSelectedLocation: (locationId: number | undefined) => void;
  setSelectedHospitalName: (hospitalName: string | undefined) => void;
  refetchHospitals: () => void;
}

const LocationContext = createContext<LocationContextType | undefined>(
  undefined,
);

export function LocationProvider({ children }: { children: React.ReactNode }) {
  const [locations, setLocations] = useState<LocationOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchTrigger, setFetchTrigger] = useState(0);

  const [selectedLocationId, setSelectedLocationIdState] = useState<
    number | undefined
  >(undefined);
  const [selectedHospitalName, setSelectedHospitalName] = useState<
    string | undefined
  >(undefined);

  const parseAndSetHospitals = (response: any, savedLocationId?: string | null) => {
    let parsedLocations: LocationOption[] = [];
    if (Array.isArray(response)) {
      parsedLocations = response.flatMap(item => {
        // Handle both hospital-grouped (clinic_locations) and state-grouped (locations)
        const clinics = item.clinic_locations || item.locations || [];

        if (Array.isArray(clinics) && clinics.length > 0) {
          return clinics.map((loc: any) => {
            const hId = loc.hospital_id || item.hospital_id || item.hospitalId || item.id;
            const hName = loc.hospital_name || item.hospital_name || item.hospitalName || item.hospital?.name || "Main Hospital";
            const bName = loc.name || loc.branch_name || loc.branchName || "Main Branch";
            const sName = resolveStateName(loc, item);

            return {
              value: Number(loc.id),
              label: `${hName} — ${bName}`,
              branchId: String(item.id || item.branch_id || item.uuid || loc.id || ""),
              branchName: bName,
              hospitalName: hName,
              hospitalId: hId,
              stateName: sName,
              phone: loc.phone || item.phone || "",
              city: loc.city || item.city || "",
            };
          });
        }

        // Fallback for flat list
        if (item.id || item.location_id) {
          const hId = item.hospital_id || item.hospitalId || item.id;
          const hName = item.hospital_name || item.hospitalName || "Main Hospital";
          const bName = item.name || item.branch_name || item.branchName || "Main Branch";
          const sName = resolveStateName(item, {});

          return [{
            value: Number(item.id || item.location_id),
            label: `${hName} — ${bName}`,
            branchId: String(item.id || item.branch_id || ""),
            branchName: bName,
            hospitalName: hName,
            hospitalId: hId,
            stateName: sName,
            phone: item.phone || "",
            city: item.city || "",
          }];
        }

        return [];
      });
    }

    parsedLocations.sort((a, b) => a.label.localeCompare(b.label));

    console.log(`📍 LocationContext: Parsed ${parsedLocations.length} locations`);
    setLocations(parsedLocations);

    // Restore saved selection
    if (savedLocationId) {
      const location = parsedLocations.find((l) => l.value === Number(savedLocationId));
      if (location) {
        setSelectedLocationIdState(location.value);
        setSelectedHospitalName(location.hospitalName);
      }
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        console.log("📍 LocationContext: Fetching hospital data...");

        // 1. Fetch hospitals and branches
        const response = await AuthService.getHospitalsAndBranches();
        console.log("📍 LocationContext raw response:", response);

        if (!response) {
          console.warn("📍 LocationContext: No response from API");
          setLocations([]);
          return;
        }

        // 2. Fetch current user profile to check for branch restrictions
        let allowedIds = new Set<string | number>();
        let isSuperAdmin = false;

        try {
          const user = await AuthService.getCurrentUser();
          if (user?.username) {
            const profile = await AuthService.getProfile(user.username);

            if (profile) {
              const role = profile?.role?.toLowerCase();

              // Admin (ID 1) is SuperAdmin
              isSuperAdmin = role === 'admin' || profile?.role_id === 1;

              // If not SuperAdmin, check for location restrictions
              if (!isSuperAdmin) {
                // 1. Handle location_ids / facility_ids (numeric IDs)
                const locIds = profile.location_ids || profile.facility_ids;
                if (Array.isArray(locIds)) {
                  locIds.forEach(id => allowedIds.add(Number(id)));
                } else if (typeof locIds === 'string') {
                  const matches = locIds.match(/\d+/g);
                  if (matches) matches.forEach(id => allowedIds.add(Number(id)));
                }

                // 2. Handle associated_branch (UUID or numeric ID)
                if (profile.associated_branch) {
                  allowedIds.add(profile.associated_branch);
                  // Also add as number if it's numeric
                  const numericBranch = Number(profile.associated_branch);
                  if (!isNaN(numericBranch)) {
                    allowedIds.add(numericBranch);
                  }
                }
              }
            } else {
              console.warn("📍 LocationContext: Profile not found in RDS, using default permissions");
            }
          }
        } catch (authError) {
          console.error("📍 LocationContext Auth Error:", authError);
        }

        const savedLocationId = localStorage.getItem("selectedLocationId");

        // 3. Filter response if restricted
        let filteredResponse = response;
        if (!isSuperAdmin && allowedIds.size > 0) {
          filteredResponse = response
            .map((branch: any) => {
              const bId = Number(branch.id || branch.branch_id || branch.uuid || branch.hospital_branch_id);
              const bUuid = String(branch.id || branch.branch_id || branch.uuid || branch.hospital_branch_id);

              const isBranchAllowed = allowedIds.has(bId) || allowedIds.has(bUuid);

              // If the branch itself is allowed, keep all its locations
              if (isBranchAllowed) return branch;

              // Otherwise, filter the locations within this branch
              const filteredLocations = (branch.locations || []).filter((loc: any) =>
                allowedIds.has(Number(loc.id)) || allowedIds.has(String(loc.id))
              );

              if (filteredLocations.length > 0) {
                return { ...branch, locations: filteredLocations };
              }
              return null;
            })
            .filter(Boolean);

          console.log(`📍 LocationContext: Filtered to ${filteredResponse.length} branches and specific locations for restricted user`);
        }

        parseAndSetHospitals(filteredResponse, savedLocationId);
      } catch (error) {
        console.error("📍 LocationContext Error:", error);
        setLocations([]);
      } finally {
        setLoading(false);
      }
    };

    const token = localStorage.getItem('jwt_token');
    if (!token) {
      console.log('📍 LocationContext: No JWT token yet, skipping fetch');
      setLoading(false);
      return;
    }
    fetchData();
  }, [fetchTrigger]);

  const setSelectedLocation = (locationId: number | undefined) => {
    setSelectedLocationIdState(locationId);
    if (locationId) {
      localStorage.setItem("selectedLocationId", String(locationId));
    } else {
      localStorage.removeItem("selectedLocationId");
    }
  };

  const refetchHospitals = () => setFetchTrigger((prev) => prev + 1);

  return (
    <LocationContext.Provider
      value={{
        locations,
        selectedLocationId,
        selectedHospitalName,
        loading,
        setSelectedLocation,
        setSelectedHospitalName,
        refetchHospitals,
      }}
    >
      {children}
    </LocationContext.Provider>
  );
}

export function useLocation() {
  const context = useContext(LocationContext);
  if (context === undefined) {
    throw new Error("useLocation must be used within a LocationProvider");
  }
  return context;
}
